$component-class: 'dh-services-reviews-type-1';

.#{$component-class} {
  position: relative;
  display: block;

  // NB: no negative `z-index` here. The decorative `&__glow` overhangs ~170px above the
  // section (see `&__glow` inset). A negative host z-index pushed the whole component behind
  // its previous sibling, so that opaque neighbour painted over the overhang and hard-cut the
  // glow at the section's top edge — a visible horizontal "border". Left in normal flow, the
  // glow's own radial mask fades it out smoothly instead (no hard cut).

  // Width + horizontal gutters come from `dh-section--container` (in the markup);
  // vertical rhythm from the host `y-gap` props. `position: relative` anchors the
  // decorative shadow blobs.
  &__inner {
    position: relative;
  }

  &__grid {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-xl);
    justify-content: center;
    position: relative;
    z-index: 3;

    @include dh-md {
      flex-wrap: nowrap;
    }
  }

  // Original responsive: 2 cards/row on phones, 4/row from tablet (orig 768),
  // a single filled row of 4 from desktop (orig 1170 → dh-md).
  &__item {
    flex: 1 1 calc(50% - var(--spacing-xl) / 2);
    min-width: 0;
    max-width: calc(50% - var(--spacing-xl) / 2);

    @include dh-sm {
      flex: 1 1 calc(25% - 3 * var(--spacing-xl) / 4);
      max-width: calc(25% - 3 * var(--spacing-xl) / 4);
    }

    @include dh-md {
      flex: 1 0 0;
      max-width: none;
    }
  }

  &__card {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    box-shadow: 0 6px 20px rgb(0 0 0 / 8%);
    gap: var(--spacing-md);
    height: 100%; // fill the stretched grid cell → equal-height cards per row
    min-height: 112px;
    padding: var(--spacing-xl);
    background: var(--background-color-primary);
    border: 1px solid var(--border-color-secondary);
    border-radius: var(--radius-size-md);
    text-decoration: none;
    color: inherit;
    transition: border-color 0.15s linear;

    &:hover {
      border-color: var(--border-color-primary);
    }
  }

  // Logo + optional name on a single centered row, matching the original
  // `.service-review__row { align-items: center; justify-content: center; height: 30px }`.
  // The fixed row height keeps every card header the same height regardless of logo
  // proportions → equal-height cards. The name is logo-adjacent and only shows from
  // desktop (orig 1170 → dh-md); on phones/tablet only the logo is visible, centered.
  &__header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-sm);
    min-height: 30px;
  }

  // Platform name — small, regular weight, hidden until desktop (matches the
  // original `.service-review__name`: 12px / 16px, shown from 1170 → dh-md). Sits to
  // the right of the logo (markup order: logo, then name), vertically centered.
  &__title {
    display: none;
    margin: 0;
    font-size: map-get($font-sizes, xs);
    font-weight: map-get($font-weights, normal);
    line-height: map-get($line-heights, lg);
    color: var(--text-color-primary);
    text-align: left;
    white-space: nowrap;

    @include dh-md {
      display: block;
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;

    // Allow wide wordmark logos (Google Workspace, Chrome Web Store) to scale
    // down to the card width instead of overflowing on narrow cards.
    min-width: 0;
    max-width: 100%;

    img {
      display: block;
      max-width: 100%;
      height: auto;
    }
  }

  // Centered at every breakpoint, matching the original `.rating { justify-content: center }`.
  &__rating {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-xs);
    min-height: 21px;
  }

  &__stars {
    --dh-rating-stars-gap: var(--spacing-2xs);
  }

  &__reviews {
    font-size: map-get($font-sizes, sm);
    font-weight: map-get($font-weights, normal);
    line-height: map-get($line-heights, md);
    color: var(--text-color-primary);
    white-space: nowrap;
  }

  &__footer {
    position: relative;
    z-index: 3;
    margin-top: var(--spacing-xl);
    font-size: map-get($font-sizes, sm);
    line-height: map-get($line-heights, md);

    // Muted date/meta text via the secondary colour token, not opacity — opacity
    // halved the effective contrast of --text-color-primary on white and failed WCAG AA.
    color: var(--text-color-secondary);
    text-align: center;

    @include dh-sm {
      margin-top: var(--spacing-2xl);
    }

    @include dh-md {
      margin-top: var(--spacing-3xl);
    }
  }

  // Decorative pastel glows behind the cards (cyan left, lavender centre, peach right —
  // colours from the original). Three large overlapping FILLED ellipses + a big
  // `filter: blur()` read as one soft multi-colour band that melts together (no visible
  // circle edges). They live in this clipped layer with a radial edge `mask`:
  //   • `overflow: hidden` → the glow can never bleed onto the neighbouring sections;
  //   • the radial mask fades the glow toward ALL edges → no hard cut where it's clipped.
  &__glow {
    position: absolute;
    inset: -170px 0 0;

    // Default: in front (0) so the glow is ALWAYS fully visible — the layer overhangs ~170px
    // above the section, and at `0` a preceding block can't clip/hide that overhang.
    z-index: 0;
    overflow: hidden;
    pointer-events: none;

    // `closest-side` sizes the ellipse to the nearest edge (here the top, since the centre
    // sits high at 44%). The `transparent` stop MUST land at or before 100% of that radius,
    // otherwise the fade is still partly opaque when `overflow` clips it and a hard rectangular
    // edge shows through (the visible top "border"). Reaching `transparent` by 100% guarantees
    // the glow is fully faded on every side before the clip — a clean, soft melt into white.
    // Centred upper so the band sits behind the cards and dies out lower down.
    mask: radial-gradient(ellipse closest-side at 50% 44%, #000 50%, transparent 100%);

    // Opt-in (via the `glow-behind` prop): drop the glow behind the preceding block so a
    // full-bleed opaque block above (e.g. the `dh-form-preview-type-1` hero) paints over the
    // overhang. `-1` escapes the section's (non-stacking-context) wrappers up to the root, so
    // it lands behind that block; the cards stay above (they're `z-index: 3`).
    &--behind {
      z-index: -1;
    }
  }

  &__shadow {
    position: absolute;
    border-radius: 50%;
    opacity: 0.65;
    filter: blur(110px);

    &--1 {
      top: 200px;
      left: 3%;
      right: 30%;
      width: 68%;
      height: 200px;
      background: $dh-color-accent-glow-cyan;
    }

    &--2 {
      top: 200px;
      left: 16%;
      width: 68%;
      height: 200px;
      background: $dh-color-accent-glow-blue;
    }

    &--3 {
      top: 150px;
      right: -12%;
      left: 30%;
      width: 68%;
      height: 200px;
      background: $dh-color-accent-glow-pink;
    }
  }
}
